import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MapModalService } from '../modules/global-map/services/map-modal.service';
import { PubService } from '../modules/main-game/components/game-modals/services/pub.service';
import { NearApiServiceService } from '../modules/main-game/services/near-api-service.service';
import { TutorialService } from '../modules/main-game/services/tutorial.service';
import { logout } from '../store/auth/auth.actions';
import {
  createCompletedCharacterSlots,
  setCharacterState,
  updateCharacterSlot,
} from '../store/characterState/characterState.actions';
import { setInitialConfigurationsData } from '../store/configurationState/configurationState.actions';
import {
  setAccountState,
  setCardState,
  setRefferalState,
  setStateCondition,
} from '../store/mainState/mainState.actions';
import {
  accountEndpoints,
  adminEndpoints,
  authEndpoints,
  backpackEndpoints,
  buildingBarmanOfferEndpoints,
  buildingCraftEndpoints,
  buildingEnergyCraftEndpoints,
  calculateNearToFishDolarCurrencyEndpoint,
  cardEndpoints,
  characterEndpoints,
  checkApiHealthStatusEndpoint,
  cityEndpoints,
  configurationEndpoints,
  dailyQuestEndpoints,
  exchangerEndpoints,
  fetchRecipyChance,
  foodChallengesEndpoints,
  foodOrderEndpoints,
  globalMapEndpoints,
  lotteryExecuteEndpoint,
  openMysteryBoxEndpoint,
  referralEndpoints,
  storeEndpoints,
} from './api.endpoints';
import { ApiWebSocketService } from './api.web-socket.service';
import {
  ICurrencyCalculateRequest,
  ICurrencyCalculateResponse,
} from './interfaces/account.interface';
import {
  IApiAdminRequestBalanceBody,
  IApiAdminRequestCardBody,
} from './interfaces/admin.interface';
import {
  IApiAccelerateRequestBody,
  IApiCraftCollect,
  IApiCraftCollectRequestBody,
  IApiStartCraftRequestBody,
  IApiStartEnergyCraftRequestBody,
  IApiStartEnergyCraftResponseBody,
  IRecipyChanceRequest,
  IRecipyChanceResponse,
} from './interfaces/buildingCraft.interface';
import { ICardUI, INewCardState } from './interfaces/card.interface';
import {
  IApiCharacterInfo,
  IApiCharacterListResponse,
  ICharacterReplenishment,
  ISlotFillRequestBody,
  ISlotUnlockRequestBody,
} from './interfaces/character.interface';
import {
  IApiCityResponse,
  IBuildingCraft,
  IMysteryBoxOpenRequest,
  IMysteryBoxOpenResponse,
} from './interfaces/city.interface';
import {
  buildingsType,
  IApiAccountResponse,
  IApiConfigurationResponse,
  IStoreBuyRequestBody,
  IStoreBuyResponse,
  ITutorial,
} from './interfaces/configuration.interface';
import {
  IMapMatrixRequest,
  IMapMatrixResponse,
  IMapUnlockLandRequest,
} from './interfaces/global-map.interface';
import {
  foodOrderActionType,
  IDefaultPagination,
  IExchangerExecuteRequest,
  IExchangerExecuteResponse,
  IExchangerFetchResponse,
  ILotteryRequest,
  ILotteryResponse,
  IPubBarmanOffer,
  IPubBarmanOfferExecute,
  IPubBarmanOfferFetch,
  IPubChallengeActive,
  IPubChallengeExecute,
  IPubChallengeExecuteResponse,
  IPubChallengeHistory,
  IPubDailyQuest,
  IPubOrder,
  IPubOrderCancelExecuteResponse,
  IPubOrderCancelOrExecuteRequestBody,
  IPubOrderChallengesFetchRequestBody,
  IPubStartDailyQuest,
} from './interfaces/pub.interface';
import { IApiReferralState } from './interfaces/referral.interface';

interface IApiRefreshResponse {
  accessToken: string;
  expirationAccessToken: string;
  expirationRefreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public mapConfig: IMapMatrixRequest = {
    coordinateX: 0,
    coordinateY: 0,
    // for testng //radius: 6,
    radius: 6,
  };
  public fullScreenMode: boolean = false;
  public isStateFetched: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private configs: IApiConfigurationResponse | undefined;
  constructor(
    private http: HttpClient,
    private store: Store,
    private ws: ApiWebSocketService,
    private nearApiService: NearApiServiceService,
    private pubService: PubService,
    private tutorialService: TutorialService,
    private mapModalService: MapModalService
  ) {}

  getMainState(): void {
    this.checkApiHealthStatus().subscribe(
      (res) => {
        this.ws.connectWebSockets();
        this.getFoodChallengesHistory().subscribe((res) =>
          this.pubService.foodTradingChallengesHistory.next(res)
        );
        // this.getGlobalMapMatrix(this.mapConfig),
        forkJoin([
          this.accountState(),
          this.characterState(),
          this.configState(),
          this.cardState(),
          this.refferalState(),
        ]).subscribe((res) => {
          this.initTutorial(res[0].tutorial);
          this.store.dispatch(setAccountState({ responseData: res[0] }));
          this.store.dispatch(setCharacterState({ responseData: res[1] }));
          this.store.dispatch(
            createCompletedCharacterSlots({ configs: res[2] })
          );
          this.store.dispatch(
            setInitialConfigurationsData({ responseData: res[2] })
          );
          this.store.dispatch(setCardState({ responseData: res[3] }));
          // this.store.dispatch(setInitialMapData({ responseData: res[4] }));
          this.store.dispatch(setRefferalState({ responseData: res[4] }));
          this.store.dispatch(setStateCondition({ ready: true }));

          this.configs = res[2];

          /// init wallet ///
          this.nearApiService.initWallet(
            'Fishmaster',
            res[2].blockchainConfigurations.find((el) => el.type === 'NEAR'),
            res[0]
          );
          this.isStateFetched.next(true);
        });
      },
      (err) => {
        this.store.dispatch(logout());
      }
    );
  }
  initTutorial(tutorial: ITutorial): void {
    // this.tutorialService.status = 'IN_PROCESS';
    // this.tutorialService.goToNextStep(22, true);
    this.tutorialService.status = tutorial.status;
    this.tutorialService.goToNextStep(
      tutorial.currentStep,
      tutorial.currentStep !== 1
    );
    if (tutorial.currentStep === 1) {
      this.mapModalService.currentModalType = 'welcome';
    } else {
    }
  }

  getRecipyChance(
    building?: buildingsType,
    buildingId?: number
  ): Observable<IRecipyChanceResponse[]> {
    const requestBody: IRecipyChanceRequest = {
      // recipeConfigurationId: recipyConfigurationId,
      building: building ? building : 'LAKE',
      // buildingId: buildingId ? buildingId : 1,
    };
    return this.http.post<IRecipyChanceResponse[]>(
      environment.apiUrl + fetchRecipyChance,
      requestBody
    );
  }

  calculateCurrency(
    body: ICurrencyCalculateRequest
  ): Observable<ICurrencyCalculateResponse> {
    return this.http.post<ICurrencyCalculateResponse>(
      environment.apiUrl + calculateNearToFishDolarCurrencyEndpoint,
      body
    );
  }

  playLottery(config: ILotteryRequest): Observable<ILotteryResponse> {
    return this.http.post<ILotteryResponse>(
      environment.apiUrl + lotteryExecuteEndpoint,
      config
    );
  }

  getPubBarmanOffers(
    requestBody?: IPubBarmanOfferFetch
  ): Observable<IPubBarmanOffer[]> {
    return this.http.post<IPubBarmanOffer[]>(
      environment.apiUrl + buildingBarmanOfferEndpoints.post.getOffers,
      { buildingId: 1 }
    );
  }
  buyDishes(
    buildingId: number,
    characterId: number,
    configurationId: string
  ): Observable<IPubBarmanOfferExecute> {
    return this.http.post<IPubBarmanOfferExecute>(
      environment.apiUrl + buildingBarmanOfferEndpoints.post.buyDishes,
      { characterId, buildingId, configurationId }
    );
  }

  checkApiHealthStatus(): Observable<Array<number>> {
    return this.http.get<Array<number>>(
      environment.apiUrl + checkApiHealthStatusEndpoint
    );
  }
  auth(
    type: 'refresh' | 'logout',
    params?: string
  ): Observable<IApiRefreshResponse | {} | boolean> {
    switch (type) {
      case 'refresh':
        return this.http.post(
          environment.apiUrl +
            authEndpoints.post.refreshToken +
            localStorage['refreshToken'],
          {}
        );
      case 'logout':
        return this.http.post(
          environment.apiUrl + authEndpoints.post.logout,
          {}
        );
      default:
        return new BehaviorSubject<boolean>(false);
    }
  }
  getGlobalMapMatrix(
    matrixConfig: IMapMatrixRequest
  ): Observable<IMapMatrixResponse[]> {
    return this.http.post<IMapMatrixResponse[]>(
      environment.apiUrl + globalMapEndpoints.post.matrix,
      matrixConfig
    );
  }
  // getUpdatedMap(matrixConfig: IMapMatrixRequest): void {
  //   this.http
  //     .post<IMapMatrixResponse[]>(
  //       environment.apiUrl + globalMapEndpoints.post.matrix,
  //       matrixConfig
  //     )
  //     .subscribe((res) => {
  //       this.store.dispatch(updateMapData({ responseData: res }));
  //     });
  // }

  unlockMapLand(
    unlockLandXY: IMapUnlockLandRequest
  ): Observable<IMapMatrixResponse> {
    return this.http.post<IMapMatrixResponse>(
      environment.apiUrl + globalMapEndpoints.post.unlock,
      unlockLandXY
    );
  }
  accountState(): Observable<IApiAccountResponse> {
    return this.http.get<IApiAccountResponse>(
      environment.apiUrl + accountEndpoints.get.accountInfo
    );
  }

  characterState(): Observable<IApiCharacterListResponse> {
    return this.http.get<IApiCharacterListResponse>(
      environment.apiUrl + characterEndpoints.get.characterList
    );
  }

  configState(): Observable<IApiConfigurationResponse> {
    return this.http.get<IApiConfigurationResponse>(
      environment.apiUrl + configurationEndpoints.get.configuration
    );
  }

  cardState(): Observable<INewCardState> {
    return this.http.get<INewCardState>(
      environment.apiUrl + cardEndpoints.get.cardState
    );
  }

  cityState(cityId: number): Observable<IApiCityResponse> {
    return this.http.get<IApiCityResponse>(
      `${environment.apiUrl + cityEndpoints.get.city}${cityId}`
    );
  }
  refferalState(): Observable<IApiReferralState> {
    return this.http.get<IApiReferralState>(
      environment.apiUrl + referralEndpoints.get.referral
    );
  }

  dailyQuestActions(
    action: 'start' | 'collect' | 'cancel' | 'fetch',
    buildingDailyTaskId?: number | string,
    requestBody?: IPubStartDailyQuest,
    buildingId?: number
  ): Observable<IPubDailyQuest | IPubDailyQuest[]> {
    // if (action === 'update') {
    //   return this.http.get<IPubDailyQuest>(
    //     environment.apiUrl + 'building/1/' + dailyQuestEndpoints.get
    //   );
    // }
    if (action === 'fetch') {
      return this.http.post<IPubDailyQuest[]>(
        environment.apiUrl +
          dailyQuestEndpoints.post.dailyQuestAction +
          'fetch',
        { buildingId: 1 }
      );
    }
    if (action === 'start' && requestBody) {
      return this.http.post<IPubDailyQuest>(
        environment.apiUrl +
          dailyQuestEndpoints.post.dailyQuestAction +
          'start',
        requestBody
      );
    }
    if (buildingDailyTaskId && (action === 'cancel' || action === 'collect')) {
      return this.http.post<IPubDailyQuest>(
        environment.apiUrl +
          dailyQuestEndpoints.post.dailyQuestAction +
          `${buildingDailyTaskId}/${action}`,
        null
      );
    }
    return new Observable(undefined);
  }

  foodOrdersActions(
    action: foodOrderActionType,
    body:
      | IPubOrderChallengesFetchRequestBody
      | IPubOrderCancelOrExecuteRequestBody
  ): Observable<IPubOrder[] | IPubOrderCancelExecuteResponse> {
    return this.http.post<IPubOrder[] | IPubOrderCancelExecuteResponse>(
      environment.apiUrl + foodOrderEndpoints.post[action],
      body
    );
  }

  public getChallengesList(characterId?: number, fetchHistory?: boolean): void {
    this.getFoodChallenges({
      characterId: characterId ? characterId : null,
      buildingId: 1,
    }).subscribe((res) => {
      let filteredCheckpoints = {
        ...res,
        data: {
          ...res.data,
          timeCheckpoints: res.data.timeCheckpoints.filter(
            (checkpoint) => checkpoint.time > Date.now().valueOf()
          ),
        },
      };
      this.pubService.foodTradingChallengesActive.next(filteredCheckpoints);
    });
    if (fetchHistory) {
      this.getFoodChallengesHistory().subscribe((res) => {
        this.pubService.foodTradingChallengesHistory.next(res);
      });
    }
  }

  getFoodChallenges(
    body: IPubOrderChallengesFetchRequestBody
  ): Observable<IPubChallengeActive> {
    return this.http.post<IPubChallengeActive>(
      environment.apiUrl + foodChallengesEndpoints.post.fetch,
      body
    );
  }
  executeFoodChallenge(
    body: IPubChallengeExecute
  ): Observable<IPubChallengeExecuteResponse> {
    return this.http.post<IPubChallengeExecuteResponse>(
      environment.apiUrl + foodChallengesEndpoints.post.execute,
      body
    );
  }
  getFoodChallengesHistory(): Observable<
    IDefaultPagination<IPubChallengeHistory>
  > {
    return this.http.get<IDefaultPagination<IPubChallengeHistory>>(
      environment.apiUrl + foodChallengesEndpoints.get.history
    );
  }
  getBackpackCards(): Observable<ICardUI[]> {
    return this.http.get<ICardUI[]>(
      environment.apiUrl + backpackEndpoints.get.fetch
    );
  }
  characterSlotActions(
    type: 'slot' | 'skill',
    action: 'unlock' | 'fill' | 'free-up' | 'redetermine',
    slotNumber?: number,
    reqBody?: ISlotFillRequestBody | ISlotUnlockRequestBody
  ): void {
    let req: Observable<IApiCharacterInfo> | undefined;
    if ((action === 'free-up' || action === 'redetermine') && slotNumber) {
      req = this.http.post<IApiCharacterInfo>(
        `${environment.apiUrl}${characterEndpoints.post[type]}${slotNumber}/${action}`,
        {}
      );
    } else {
      if (reqBody)
        req = this.http.post<IApiCharacterInfo>(
          `${environment.apiUrl}${characterEndpoints.post[type]}${action}`,
          reqBody
        );
    }
    if (req)
      req.subscribe((res) => {
        this.store.dispatch(updateCharacterSlot({ slot: res }));
        if (this.configs)
          this.store.dispatch(
            createCompletedCharacterSlots({ configs: this.configs })
          );
      });
  }
  public characterReplenishmentActions(
    type: 'energyReplenishment' | 'expirenceReplenishment',
    body: ICharacterReplenishment
  ): Observable<boolean> {
    return this.http.post<boolean>(
      environment.apiUrl + characterEndpoints.post[type],
      body
    );
  }
  public exchangerExchangeActions(action: {
    type: 'fetch' | 'execute';
    body: IExchangerExecuteRequest;
    exchangeAction: 'exchange' | 'offer';
  }): Observable<IExchangerExecuteResponse | IExchangerFetchResponse[]> {
    return this.http.post<
      IExchangerExecuteResponse | IExchangerFetchResponse[]
    >(
      environment.apiUrl +
        exchangerEndpoints.post[action.exchangeAction][action.type],
      action.body
    );
  }
  public openMysteryBox(
    body: IMysteryBoxOpenRequest
  ): Observable<IMysteryBoxOpenResponse> {
    return this.http.post<IMysteryBoxOpenResponse>(
      environment.apiUrl + openMysteryBoxEndpoint,
      body
    );
  }
  public buildingCraftActions(
    action: 'startCraft' | 'collect' | 'accelerate',
    requestBody:
      | IApiStartCraftRequestBody
      | IApiCraftCollectRequestBody
      | IApiAccelerateRequestBody
  ): Observable<IBuildingCraft | IApiCraftCollect | IBuildingCraft | any> {
    return this.http.post(
      environment.apiUrl + buildingCraftEndpoints.post[action],
      requestBody
    );
  }
  buildingEnergyCraftActions(
    requestBody: IApiStartEnergyCraftRequestBody
  ): Observable<IApiStartEnergyCraftResponseBody> {
    return this.http.post<IApiStartEnergyCraftResponseBody>(
      environment.apiUrl + buildingEnergyCraftEndpoints.post.startCraft,
      requestBody
    );
  }
  adminActions(action: {
    type: 'card' | 'accountReset' | 'balance' | 'energyReset';
    body: IApiAdminRequestBalanceBody | IApiAdminRequestCardBody | null;
  }): Observable<object> {
    return this.http.post(
      environment.apiUrl + adminEndpoints.post[action.type],
      action.body
    );
  }
  buyPack(body: IStoreBuyRequestBody): Observable<IStoreBuyResponse> {
    return this.http.post<IStoreBuyResponse>(
      environment.apiUrl + storeEndpoints.post.buy,
      body
    );
  }
  unsubscribe(subscruptions: Subscription[]): void {
    subscruptions.forEach((subsc) => subsc.unsubscribe());
  }
}
